import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, Loader2, Tag, Palette } from 'lucide-react';
import { GALLERY } from '@/constants/testIds';
import Placeholder from './Placeholder';
import { useCatalog } from '@/hooks/useCatalog';
import { productImageUrl } from '@/lib/imageUrl';

// Words that qualify a tag as a "colour" (used to split colour chips from tag chips)
const COLOR_WORDS = new Set([
  'red','crimson','maroon','burgundy','wine','ruby',
  'pink','fuchsia','magenta','rose','blush','peach','coral',
  'orange','marigold','rust','copper',
  'yellow','gold','mustard','marigold',
  'green','emerald','sage','mint','olive','teal',
  'blue','navy','royal','sapphire','sky','turquoise','indigo',
  'purple','plum','lavender','lilac','aubergine','violet','mauve',
  'black','charcoal',
  'white','ivory','cream','off-white',
  'silver','grey','gray','pearl',
  'brown','beige','tan','nude','bronze',
  'multicolor','multicoloured','rainbow'
]);
const isColorTag = (t) => COLOR_WORDS.has(String(t).toLowerCase().trim());

const Tile = ({ item, idx }) => {
  const ratio = item.ratio || '3/4';
  const wrapper = 'shrink-0 w-[78vw] sm:w-[52vw] md:w-[36vw] lg:w-[26vw] xl:w-[22vw] snap-start group block relative overflow-hidden';
  const motionProps = {
    initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: (idx % 6) * 0.06, ease: [0.22, 1, 0.36, 1] },
  };
  const Inner = (
    <>
      <div className="transition-transform duration-[1200ms] ease-out group-hover:scale-105">
        <Placeholder label={item.label} tone={item.tone} ratio={ratio} image={productImageUrl(item)} alt={item.label} sold={!!item.sold} />
      </div>
      {item.sub && (
        <div className="absolute inset-x-0 bottom-0 px-4 py-3 bg-gradient-to-t from-black/70 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059]">{item.label}</div>
          <div className="text-xs text-white/90 mt-0.5">{item.sub}</div>
        </div>
      )}
      {(item.image_seo_name || item.image) && (
        <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
          <ArrowUpRight className="w-4 h-4 text-maroon" />
        </div>
      )}
    </>
  );
  if (item.image_seo_name || item.image) {
    return (
      <motion.div {...motionProps} className={wrapper} data-testid={GALLERY.tile(item.slug)}>
        <Link to={`/product/${item.slug}`} className="block relative">{Inner}</Link>
      </motion.div>
    );
  }
  return (
    <motion.a href="#appointment" data-testid={GALLERY.tile(item.slug)} {...motionProps} className={wrapper}>{Inner}</motion.a>
  );
};

export const Gallery = () => {
  const { products, categories, loading } = useCatalog();
  const [cat, setCat] = useState('');
  const [tag, setTag] = useState('');
  const [chipView, setChipView] = useState('tags'); // 'tags' | 'colours'
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!cat && !tag && categories.length) {
      const withProducts = categories.find((c) => products.some((p) => p.category === c.key));
      setCat(withProducts?.key || categories[0]?.key || '');
    }
  }, [cat, tag, categories, products]);

  // Listen for external category-selection requests (e.g. from FeaturedCollections tiles)
  useEffect(() => {
    const onSelect = (e) => {
      if (e?.detail) { setCat(e.detail); setTag(''); }
    };
    window.addEventListener('tc:selectCategory', onSelect);
    return () => window.removeEventListener('tc:selectCategory', onSelect);
  }, []);

  // Products scoped to the current category (used to derive category-specific chips)
  const catProducts = useMemo(() => (
    cat ? products.filter((p) => p.category === cat) : products
  ), [products, cat]);

  // Split tags into "colours" and "other tags" — scoped to the SELECTED collection
  // Colours: show all (any count). Tags: show only when count > 2 for THIS collection.
  const { tagChips, colourChips } = useMemo(() => {
    const tagCounts = {};
    const colourCounts = {};
    catProducts.forEach((p) => (p.tags || []).forEach((raw) => {
      const t = String(raw).trim();
      if (!t) return;
      if (isColorTag(t)) {
        const k = t.toLowerCase();
        colourCounts[k] = (colourCounts[k] || 0) + 1;
      } else {
        tagCounts[t] = (tagCounts[t] || 0) + 1;
      }
    }));
    return {
      tagChips: Object.entries(tagCounts).filter(([, c]) => c > 2).sort((a, b) => b[1] - a[1]),
      colourChips: Object.entries(colourCounts).sort((a, b) => b[1] - a[1]),
    };
  }, [catProducts]);

  const activeChips = chipView === 'colours' ? colourChips : tagChips;

  const HOME_LIMIT = 8; // keep home light — full list lives on /category/:key

  const { items, hasMore } = useMemo(() => {
    let base;
    if (tag) {
      const needle = tag.toLowerCase();
      // When a tag is picked, keep the filter scoped to the currently selected collection
      const scope = cat ? catProducts : products;
      base = scope.filter((p) => (p.tags || []).some((t) => String(t).toLowerCase() === needle));
    } else {
      base = catProducts;
    }
    return { items: base.slice(0, HOME_LIMIT), hasMore: base.length > HOME_LIMIT };
  }, [products, catProducts, cat, tag]);

  // When user selects a tag, keep them within the current collection
  const pickCategory = (k) => { setCat(k); setTag(''); };
  const pickTag = (t) => {
    if (tag === t) { setTag(''); return; }
    setTag(t);
  };

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8 * (dir === 'next' ? 1 : -1), behavior: 'smooth' });
  };

  return (
    <section id="gallery" data-testid={GALLERY.root} className="py-24 lg:py-32 bg-surface">
      <div className="container-luxe">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <span className="overline">The Lookbook</span>
            <h2 className="font-display text-4xl lg:text-5xl text-charcoal mt-3 leading-tight">
              A glimpse inside the <em className="text-maroon not-italic font-medium">closet</em>.
            </h2>
          </div>
          <p className="max-w-md text-charcoal/70 leading-relaxed">
            Wander through colours, weaves and stories that make up our ever-growing collection.
          </p>
        </div>

        {/* Category tabs + arrows */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 border-t border-brandborder/70 pt-5">
          <div className="flex flex-wrap items-center gap-2 lg:gap-3">
            {categories.map((c) => {
              const active = !tag && cat === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`gallery-tab-${c.key}`}
                  onClick={() => pickCategory(c.key)}
                  className={`px-5 lg:px-7 py-2.5 text-xs uppercase tracking-[0.2em] transition-all duration-500 border ${
                    active ? 'bg-burgundy text-ivory border-burgundy' : 'bg-transparent text-charcoal/70 border-brandborder hover:border-maroon hover:text-maroon'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => scrollBy('prev')} data-testid="gallery-prev" aria-label="Scroll left" className="w-11 h-11 border border-brandborder hover:border-maroon hover:text-maroon text-charcoal/80 flex items-center justify-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scrollBy('next')} data-testid="gallery-next" aria-label="Scroll right" className="w-11 h-11 border border-brandborder hover:border-maroon hover:text-maroon text-charcoal/80 flex items-center justify-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Tag / Colour filter switcher */}
        {(tagChips.length > 0 || colourChips.length > 0) && (
          <div className="border-b border-brandborder/70 pb-5 mb-10 lg:mb-14" data-testid="gallery-tags">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <button
                onClick={() => setChipView('tags')}
                data-testid="chip-view-tags"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                  chipView === 'tags' ? 'border-maroon text-maroon bg-blush' : 'border-transparent text-charcoal/55 hover:text-maroon'
                }`}
              >
                <Tag className="w-3.5 h-3.5" /> Browse by tag
              </button>
              <button
                onClick={() => setChipView('colours')}
                data-testid="chip-view-colours"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                  chipView === 'colours' ? 'border-maroon text-maroon bg-blush' : 'border-transparent text-charcoal/55 hover:text-maroon'
                }`}
              >
                <Palette className="w-3.5 h-3.5" /> Browse by colour
              </button>
              {tag && (
                <button onClick={() => setTag('')} data-testid="gallery-tag-clear" className="ml-auto text-[11px] uppercase tracking-[0.15em] text-charcoal/55 hover:text-maroon underline-offset-4 hover:underline">
                  clear
                </button>
              )}
            </div>
            <div className="-mx-6 md:-mx-10 lg:-mx-16">
              <div className="no-scrollbar flex flex-wrap md:flex-nowrap gap-2 overflow-x-auto px-6 md:px-10 lg:px-16 pb-1" data-testid={chipView === 'colours' ? 'gallery-colour-chips' : 'gallery-tag-chips'}>
                {activeChips.length === 0 ? (
                  <span className="text-xs text-charcoal/50 italic">
                    {chipView === 'tags' ? 'No tags with 3+ products yet.' : 'No colours tagged yet.'}
                  </span>
                ) : activeChips.map(([t]) => (
                  <button
                    key={t}
                    data-testid={`gallery-${chipView === 'colours' ? 'colour' : 'tag'}-${t}`}
                    onClick={() => pickTag(t)}
                    className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${
                      tag === t ? 'border-maroon text-maroon bg-blush' : 'border-brandborder text-charcoal/65 hover:border-maroon hover:text-maroon'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active filter breadcrumb */}
        {tag && (
          <div className="mb-6 text-xs uppercase tracking-[0.2em] text-charcoal/60">
            Filtering by tag <span className="text-maroon">“{tag}”</span>
          </div>
        )}

        <div className="relative -mx-6 md:-mx-10 lg:-mx-16">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-charcoal/55">No pieces match this filter yet — try another tag.</div>
          ) : (
            <AnimatePresence mode="wait">
              {/* content below */}
              <motion.div
                key={tag ? `tag:${tag}` : `cat:${cat}`}
                ref={scrollerRef}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="no-scrollbar flex gap-5 lg:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 md:px-10 lg:px-16 pb-4"
              >
                {items.map((it, i) => <Tile key={it.slug} item={it} idx={i} />)}
                {hasMore && cat && !tag && (
                  <Link
                    to={`/category/${cat}`}
                    data-testid="gallery-see-all"
                    className="shrink-0 w-[78vw] sm:w-[52vw] md:w-[36vw] lg:w-[26vw] xl:w-[22vw] snap-start group relative flex flex-col justify-center items-center aspect-[3/4] bg-blush border border-brandborder text-charcoal hover:bg-maroon hover:text-ivory transition-colors duration-500"
                  >
                    <span className="overline mb-3 group-hover:text-gold">{categories.find((c) => c.key === cat)?.label}</span>
                    <span className="font-display text-3xl lg:text-4xl text-center leading-tight px-6">
                      See the full<br/>collection
                    </span>
                    <ArrowUpRight className="w-6 h-6 mt-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                )}
                <div className="shrink-0 w-2 md:w-6" aria-hidden />
              </motion.div>
            </AnimatePresence>
          )}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 md:w-16 bg-gradient-to-r from-surface to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 md:w-16 bg-gradient-to-l from-surface to-transparent" />
        </div>

        {/* Glimpse note */}
        <p className="mt-10 lg:mt-14 max-w-3xl text-sm lg:text-base text-charcoal/70 italic font-display border-l-2 border-gold pl-5">
          What you see here is only a glimpse of what we offer. Stop by our boutique to
          discover our complete collection and find the perfect outfit for your special
          occasion.
        </p>
      </div>
    </section>
  );
};

export default Gallery;
